import { pushTextUndoQueue } from './text/textUndoRedoUtils.js';
import {
  addText,
  mouseHelper,
  scene,
  size,
  toolbarActive,
  updateDecals,
  updateLogoChange,
} from './webGL.js';

const THREE = window.THREE;
export var textsOfHandling = {
  texts: [],
};
const defaultScale = 1;

// Memory cleanup utilities
const cleanupCanvas = (canvas) => {
  if (canvas) {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    canvas.width = 0;
    canvas.height = 0;
  }
};

const cleanupTexture = (texture) => {
  if (texture) {
    if (texture.image && texture.image.src) {
      texture.image.src = '';
    }
    texture.dispose();
  }
};

// Text rendering configuration
const TEXT_CONFIGS = {
  renderTextOnly: {
    baseWidth: 900,
    baseHeight: 600,
    baseFontSize: 150,
    charWidthMultiplier: 60,
    lineHeightMultiplier: 300,
    fontSizeMultiplier: 0.6,
    minFontSize: 60,
  },
  drawText: {
    baseWidth: 300,
    baseHeight: 200,
    baseFontSize: 50,
    charWidthMultiplier: 20,
    lineHeightMultiplier: 100,
    fontSizeMultiplier: 0.4,
    minFontSize: 20,
  },
  renderTextImage: {
    baseWidth: 300,
    baseHeight: 200,
    baseFontSize: 50,
    charWidthMultiplier: 20,
    lineHeightMultiplier: 100,
    fontSizeMultiplier: 0.3,
    minFontSize: 20,
  },
};

// Common utility functions for text rendering
const TextRenderer = {
  // Calculate canvas dimensions based on text content
  calculateCanvasDimensions(text, config) {
    const textLength = text ? text.length : 0;
    const lines = text ? text.split('\n') : [''];

    const canvasWidth = Math.max(
      config.baseWidth,
      Math.min(textLength * config.charWidthMultiplier, 2000),
    );
    const canvasHeight = config.baseHeight + (lines.length - 1) * config.lineHeightMultiplier;
    const fontSize = Math.max(
      config.minFontSize,
      Math.min(config.baseFontSize, canvasWidth / (textLength * config.fontSizeMultiplier)),
    );

    return { canvasWidth, canvasHeight, fontSize, lines };
  },

  // Setup canvas context with proper scaling and clearing
  setupCanvasContext(canvas, ctx, canvasWidth, canvasHeight, isFlip) {
    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      if (typeof isFlip === 'boolean') {
        isFlip = false; // Reset flip state when canvas is resized
      }
    }

    if (isFlip === false) {
      ctx.translate(0, canvas.height);
      ctx.scale(1, -1);
      if (typeof isFlip === 'boolean') {
        isFlip = true;
      }
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  },

  // Render text with proper line handling
  renderText(ctx, text, font, color, fontSize, canvasWidth, canvasHeight, lines) {
    ctx.font = fontSize + 'px ' + font;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';

    if (lines.length > 1) {
      // Text has explicit line breaks (Enter key was pressed)
      const lineHeight = fontSize * 1.2;
      const startY = canvasHeight / 2 - ((lines.length - 1) * lineHeight) / 2;

      lines.forEach((line, index) => {
        ctx.fillText(line, canvasWidth / 2, startY + index * lineHeight);
      });
    } else {
      // Single line text - no automatic wrapping
      ctx.fillText(text, canvasWidth / 2, canvasHeight / 2);
    }
  },
};

// Common utility functions for 3D text operations
const Text3DUtils = {
  // Create orientation with rotation applied
  createOrientation(logo, mouseHelper) {
    const orientation = new THREE.Euler();
    orientation.copy(mouseHelper.rotation);
    orientation.z += (logo.Rotate * Math.PI) / 180;
    return orientation;
  },

  // Set scale for decal mesh
  setDecalScale(logo, size) {
    const scale = logo.Scale;
    size.set(scale * defaultScale, scale * defaultScale, scale * defaultScale);
  },

  // Create decal mesh with common properties
  createDecalMesh(logo, intersection, orientation, size) {
    const decalMesh = new THREE.Mesh(
      new THREE.DecalGeometry(intersection.mesh, logo.Position, orientation, size),
      logo.decalMaterial,
    );

    decalMesh.logo = logo;
    logo.meshID = intersection.mesh.meshId;

    return decalMesh;
  },
};

export function renderTextOnly(logo) {
  window.currentLogo = logo;

  // Cleanup previous canvas if exists
  if (window.currentLogo.previousCanvas) {
    cleanupCanvas(window.currentLogo.previousCanvas);
    window.currentLogo.previousCanvas = null;
  }

  let canvas = document.createElement('CANVAS');

  // Use shared utility to calculate dimensions
  const config = TEXT_CONFIGS.renderTextOnly;
  const { canvasWidth, canvasHeight, fontSize, lines } = TextRenderer.calculateCanvasDimensions(
    logo.Text,
    config,
  );

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  let ctx = canvas.getContext('2d');
  ctx.translate(0, canvas.height);
  ctx.scale(1, -1);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Use shared utility to render text
  TextRenderer.renderText(
    ctx,
    logo.Text,
    logo.Font,
    logo.Color,
    fontSize,
    canvasWidth,
    canvasHeight,
    lines,
  );

  // Cleanup previous texture if exists
  if (window.currentLogo.previousTexture) {
    cleanupTexture(window.currentLogo.previousTexture);
    window.currentLogo.previousTexture = null;
  }

  const textureCanvas = new THREE.CanvasTexture(ctx.canvas);
  textureCanvas.needsUpdate = true; // Fix: Set to true to ensure texture updates

  // Store reference for cleanup
  window.currentLogo.previousTexture = textureCanvas;
  window.currentLogo.previousCanvas = canvas;

  // Create or update the decal material
  if (!window.currentLogo.decalMaterial) {
    window.currentLogo.decalMaterial = new THREE.MeshPhongMaterial({
      // specular: 0x444444,
      map: textureCanvas,
      normalScale: new THREE.Vector2(1, 1),
      shininess: 50,
      transparent: true,
      opacity: 1,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      wireframe: false,
    });
  } else {
    // Update existing material with new texture
    window.currentLogo.decalMaterial.map = textureCanvas;
    window.currentLogo.decalMaterial.needsUpdate = true;
  }

  // If there's an existing decal mesh, update its material
  if (window.currentLogo.decalMesh) {
    window.currentLogo.decalMesh.material = window.currentLogo.decalMaterial;
    window.currentLogo.decalMesh.material.needsUpdate = true;
  }

  return window.currentLogo;
}

var isFlip = false;
export function drawText(logoText) {
  let canvas = document.getElementById('textCanvas');
  let ctx = canvas.getContext('2d');

  // Use shared utility to calculate dimensions
  const config = TEXT_CONFIGS.drawText;
  const { canvasWidth, canvasHeight, fontSize, lines } = TextRenderer.calculateCanvasDimensions(
    logoText.Text,
    config,
  );

  // Use shared utility to setup canvas context
  TextRenderer.setupCanvasContext(canvas, ctx, canvasWidth, canvasHeight, isFlip);

  // Update isFlip state
  if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
    isFlip = false; // Reset flip state when canvas is resized
  }
  if (isFlip === false) {
    isFlip = true;
  }

  // Use shared utility to render text
  TextRenderer.renderText(
    ctx,
    logoText.Text,
    logoText.Font,
    logoText.Color,
    fontSize,
    canvasWidth,
    canvasHeight,
    lines,
  );
}

export function renderTextImage(txt, color, font, id, position, isUpdate = true) {
  let canvas = document.getElementById('textCanvas');
  let ctx = canvas.getContext('2d');

  // Use shared utility to calculate dimensions
  const config = TEXT_CONFIGS.renderTextOnly;
  const { canvasWidth, canvasHeight, fontSize, lines } = TextRenderer.calculateCanvasDimensions(
    txt,
    config,
  );

  // Use shared utility to setup canvas context
  TextRenderer.setupCanvasContext(canvas, ctx, canvasWidth, canvasHeight, isFlip);

  // Update isFlip state
  if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
    isFlip = false; // Reset flip state when canvas is resized
  }
  if (isFlip === false) {
    isFlip = true;
  }

  // Use shared utility to render text
  TextRenderer.renderText(ctx, txt, font, color, fontSize, canvasWidth, canvasHeight, lines);

  for (let decal of textsOfHandling.texts) {
    if (decal.logo.Id === id) {
      window.currentLogo = decal.logo;
      break;
    }
  }

  if (
    window.currentLogo != null &&
    window.currentLogo.IsText === true &&
    window.currentLogo.Id === id
  ) {
    var textureCanvas = new THREE.CanvasTexture(ctx.canvas);
    textureCanvas.needsUpdate = false;
    window.currentLogo.Text = txt;
    window.currentLogo.Color = color;
    window.currentLogo.Font = font;
    window.currentLogo.decalMaterial.map = textureCanvas;
    if (position) {
      window.currentLogo.Position = position;
    }

    isUpdate && updateLogoChange(window.currentLogo);
  }
}

export function insertTextOnModel(logo, intersection) {
  if (!logo) return;

  removeDuplicateDecals(logo.Id);

  const isInitialPlacement = logo.Position === null || typeof logo.Position === 'undefined';

  if (isInitialPlacement) {
    // Initial placement - set position and orientation
    logo.Position = new THREE.Vector3(
      intersection.point.x,
      intersection.point.y,
      intersection.point.z,
    );
    logo.Orientation = new THREE.Euler();
    logo.Orientation.copy(mouseHelper.rotation);
  } else if (toolbarActive.value === 'text') {
    // Replacing - update position and orientation
    scene.remove(logo.decalMesh);
    logo.Position.copy(intersection.point);
    logo.Orientation.copy(mouseHelper.rotation);
  } else {
    // Not in text mode, skip replacement
    return;
  }
  // Common logic for both initial placement and replacement
  const orientation = Text3DUtils.createOrientation(logo, mouseHelper);
  Text3DUtils.setDecalScale(logo, size);
  const newDecalMesh = Text3DUtils.createDecalMesh(logo, intersection, orientation, size);

  // Safely add the new decal mesh
  safeAddDecalMesh(logo, newDecalMesh);
  updateDecals();
}

// ----------------------------------------
// Shared helpers for decal mesh cleanup
// ----------------------------------------
export function disposeDecalMesh(mesh) {
  if (!mesh) return;
  scene.remove(mesh);
  if (mesh.geometry) {
    mesh.geometry.dispose();
  }
  if (mesh.material) {
    mesh.material.dispose();
  }
}

export function clearLogoDecal(logo) {
  if (logo && logo.decalMesh) {
    disposeDecalMesh(logo.decalMesh);
    logo.decalMesh = null;
  }
}

export function clearTexts() {
  // Properly remove all decal meshes from scene and dispose resources
  textsOfHandling.texts.forEach(function (d) {
    if (d && d.logo && d.logo.decalMesh) {
      clearLogoDecal(d.logo);
    }

    // Cleanup additional resources
    if (d && d.logo) {
      if (d.logo.previousTexture) {
        cleanupTexture(d.logo.previousTexture);
        d.logo.previousTexture = null;
      }
      if (d.logo.previousCanvas) {
        cleanupCanvas(d.logo.previousCanvas);
        d.logo.previousCanvas = null;
      }
    }
  });

  // Clear the texts array
  textsOfHandling.texts = [];
  window.decals = window.decals.filter((d) => d.logo && d.logo.IsText !== true);
}

export function clearText(text) {
  // Properly remove decal mesh from scene
  clearLogoDecal(text);

  // Reset position and orientation
  text.Position = null;
  text.Orientation = null;

  // Remove from texts array
  const existingIndex = textsOfHandling.texts.findIndex(
    (d) => d && d.logo && d.logo.Id === text.Id,
  );
  if (existingIndex !== -1) {
    textsOfHandling.texts.splice(existingIndex, 1);
  }

  // Also remove from window.textEditor_textList if it exists
  if (window.textEditor_textList) {
    const textListIndex = window.textEditor_textList.findIndex((t) => t.Id === text.Id);
    if (textListIndex !== -1) {
      window.textEditor_textList.splice(textListIndex, 1);
    }
  }

  // Also remove from window.texts if it exists
  if (window.texts) {
    const windowTextsIndex = window.texts.findIndex((t) => t.logo && t.logo.Id === text.Id);
    if (windowTextsIndex !== -1) {
      window.texts.splice(windowTextsIndex, 1);
    }
  }
}

export function deleteLogoText(logo) {
  // Add undo operation before deletion
  pushTextUndoQueue();

  // Properly remove decal mesh from scene
  clearLogoDecal(logo);

  // Remove from texts array
  const existingIndex = textsOfHandling.texts.findIndex(
    (d) => d && d.logo && d.logo.Id === logo.Id,
  );
  if (existingIndex !== -1) {
    textsOfHandling.texts.splice(existingIndex, 1);
  }

  // Also remove from window.textEditor_textList if it exists
  if (window.textEditor_textList) {
    const textListIndex = window.textEditor_textList.findIndex((t) => t.Id === logo.Id);
    if (textListIndex !== -1) {
      window.textEditor_textList.splice(textListIndex, 1);
    }
  }

  // Also remove from window.texts if it exists
  if (window.texts) {
    const windowTextsIndex = window.texts.findIndex((t) => t.logo && t.logo.Id === logo.Id);
    if (windowTextsIndex !== -1) {
      window.texts.splice(windowTextsIndex, 1);
    }
  }
}

// Helper function to check for duplicate decal meshes
export function checkForDuplicateDecals(logoId) {
  if (!logoId) return [];

  const duplicates = textsOfHandling.texts.filter((d) => d && d.logo && d.logo.Id === logoId);
  if (duplicates.length > 1) {
    return duplicates;
  }
  return [];
}

// Helper function to remove duplicate decal meshes
export function removeDuplicateDecals(logoId) {
  if (!logoId) return;

  const duplicates = checkForDuplicateDecals(logoId);
  if (duplicates.length > 1) {
    // Keep only the first one, remove the rest
    for (let i = 1; i < duplicates.length; i++) {
      const duplicate = duplicates[i];
      if (duplicate.logo && duplicate.logo.decalMesh) {
        clearLogoDecal(duplicate.logo);
      }
    }

    // Remove duplicates from texts array more efficiently
    // Find the index of the first occurrence to keep
    const firstOccurrenceIndex = textsOfHandling.texts.findIndex(
      (d) => d && d.logo && d.logo.Id === logoId,
    );

    // Remove all occurrences of this logoId
    textsOfHandling.texts = textsOfHandling.texts.filter(
      (d) => d && d.logo && d.logo.Id !== logoId,
    );

    // Add back only the first occurrence if it exists
    if (firstOccurrenceIndex !== -1 && duplicates.length > 0) {
      textsOfHandling.texts.push(duplicates[0]);
    }
  }

  // Also clean duplicates in textEditor_textList
  if (window.textEditor_textList) {
    const seen = new Set();
    window.textEditor_textList = window.textEditor_textList.filter((item) => {
      if (seen.has(item.Id)) {
        return false; // Remove duplicate
      }
      seen.add(item.Id);
      return true; // Keep first occurrence
    });
  }
}

// Helper function to safely add decal mesh
export function safeAddDecalMesh(logo, decalMesh) {
  // First, remove any existing decal mesh for this logo
  if (logo.decalMesh) {
    clearLogoDecal(logo);
  }

  // Remove any existing entries for this logo from texts array
  textsOfHandling.texts = textsOfHandling.texts.filter((d) => d && d.logo && d.logo.Id !== logo.Id);

  // Remove any duplicate decals for this logo
  removeDuplicateDecals(logo.Id);

  // Set the new decal mesh
  logo.decalMesh = decalMesh;
  addText(decalMesh);

  // Add to texts array for 3D scene selection
  if (logo.IsText) {
    textsOfHandling.texts.push(decalMesh);
  }

  return decalMesh;
}

// Function to clean up all duplicate decals (useful for debugging)
export function cleanupAllDuplicateDecals() {
  const allLogoIds = [
    ...new Set(textsOfHandling.texts.map((d) => d.logo && d.logo.Id).filter(Boolean)),
  ];
  let totalRemoved = 0;

  allLogoIds.forEach((logoId) => {
    const duplicates = checkForDuplicateDecals(logoId);
    if (duplicates.length > 1) {
      removeDuplicateDecals(logoId);
      totalRemoved += duplicates.length - 1;
    }
  });

  return totalRemoved;
}

// Function to update text in 3D scene
export function updateTextInScene(logo) {
  if (!logo || !logo.decalMesh) {
    return;
  }

  // Simply update the existing decal mesh material
  if (logo.decalMesh.material) {
    logo.decalMesh.material.needsUpdate = true;
  }
}
